import { fieldAccentColor, fieldBorderColor, fieldFill, navSelected } from '@repo/ui'

import React from 'react'

import { Controller, type Control, type FieldError } from 'react-hook-form'

import { useCountriesQuery } from 'hooks/api'

import { Typography, Stack, Autocomplete, TextField, Box } from '@mui/material'
import { ChevronDownIcon } from 'lucide-react'

// Support both controlled component mode (value/onChange) and react-hook-form mode (name/control)
interface SelectCountryFieldProps {
  label?: string
  placeholder?: string
  disabled?: boolean
  error?: FieldError
  // Controlled component props
  value?: string | null
  onChange?: (countryCode: string) => void
  // React-hook-form props
  name?: string
  control?: Control<any>
  defaultValue?: string
  /**
   * Static { code, name } list to render instead of `useCountriesQuery`'s data.
   * `useCountriesQuery` hits the AUTHENTICATED `/common/countries` endpoint
   * (`AuthGuard`-gated) — a PUBLIC caller (e.g. the self-serve signup page,
   * which has no session) must supply this instead of letting the query fire
   * and 401. When provided, the query is disabled entirely (`enabled: false`).
   */
  countriesOverride?: { code: string; name: string }[]
}

// Helper function to get flat rectangle flag image URL
export const getFlagUrl = (countryCode: string): string => {
  if (!countryCode || countryCode.length !== 2) return ''
  // Using flagcdn.com for clean, flat rectangular flags
  return `https://flagcdn.com/w20/${countryCode.toLowerCase()}.png`
}

// Match the design-system Select dropdowns (SelectField / Select): drop the menu
// below the trigger with a real 6px gap, an 8px paper radius, and an inset rounded
// item highlight on hover (list padding gives the highlight breathing room).
const countryMenuSlotProps = {
  popper: {
    // Always drop the menu BELOW the trigger with a real 6px gap, like the
    // design-system Select dropdowns (which anchor bottom and don't flip).
    placement: 'bottom-start' as const,
    modifiers: [
      { name: 'offset', options: { offset: [0, 6] } },
      { name: 'flip', enabled: false },
    ],
  },
  paper: {
    sx: (theme: any) => ({
      borderRadius: '8px',
      backgroundImage: 'none',
      // Inset the list so the rounded item highlight has breathing room, and round
      // the per-item hover/selected highlight to match SelectField / Select.
      '& .MuiAutocomplete-listbox': {
        p: 1,
      },
      '& .MuiAutocomplete-option': {
        borderRadius: '8px',
        minHeight: 40,
        // Highlight colour comes from navChrome via the theme's MuiAutocomplete listbox
        // override, so this component no longer states its own. It used to hardcode
        // primary[50] (#f6f6f6), which made the country picker the one dropdown in the app
        // highlighting rows grey while every other menu used the brand tint. Only the SHAPE
        // (rounded, min-height) is this component's business.
        '&.Mui-focused, &[aria-selected="true"], &[aria-selected="true"].Mui-focused': {
          backgroundColor: navSelected(theme),
        },
      },
    }),
  },
}

/**
 * Field metrics for the Country Autocomplete, matching the shared @repo/ui Input so it
 * lines up with the text fields beside it (Name, State, City, Postal).
 *
 * ONE definition on purpose. This component renders the same TextField twice -- once in
 * react-hook-form mode and once as a controlled component -- and each copy carried its
 * own inline `sx`. They drifted: the react-hook-form copy (used by Settings > Company >
 * General) sat at 47px/14px while the controlled copy was corrected to 49px/16px, so the
 * Country field was visibly shorter with smaller text than the Name field next to it.
 *
 * NOTE on the border literals: `theme.palette.primary[100]` can't be used here -- THIS
 * platform component's MUI theme resolves it to #3f3f46, one ramp step brighter than the
 * @repo/ui theme's #27272a that Input actually renders. Mode-aware literals guarantee
 * parity; a token would not.
 */
const fieldSx = (theme: any) => ({
  '& .MuiInputBase-root': {
    height: '49px',
    backgroundColor: theme.palette.mode === 'light' ? '#ffffff' : (theme.palette.primary?.[50] || '#f6f6f6'),
    borderRadius: '8px',
    paddingRight: '40px !important',
    paddingLeft: '11px !important',
    fontSize: '16px',
  },
  '& .MuiOutlinedInput-root': {
    // Same fill as every other field. The border was already wired to fieldChrome but the FILL was
    // not, so in light this sat white beside the #f6f6f6 text inputs next to it.
    backgroundColor: fieldFill(theme),
    '& fieldset': {
      // `fieldChrome`, not a literal. This was hardcoded `#acafb4` / `#27272a` with a
      // comment explaining they had been matched to @repo/ui Input's grey[300] / primary[100]
      // rest border — correct when written, and orphaned the moment Input moved to grey[200].
      // Copying a value to "match" another component records the match in prose and breaks it
      // silently; reading the same function cannot drift.
      borderColor: fieldBorderColor(theme),
    },
    '&:hover fieldset': {
      borderColor: fieldAccentColor(theme),
    },
    '&.Mui-focused fieldset': {
      borderColor: fieldAccentColor(theme),
    },
  },
})

export const SelectCountryField = (props: SelectCountryFieldProps) => {
  const {
    label,
    placeholder = 'Select a country',
    disabled,
    error,
    value,
    onChange,
    name,
    control,
    defaultValue,
    countriesOverride,
  } = props

  // Hooks must run unconditionally — the query itself is what's gated, via
  // `enabled`, so a public caller passing `countriesOverride` never fires it.
  const { data: fetchedCountries, isLoading: isFetching } = useCountriesQuery(!countriesOverride)
  const countries = countriesOverride ?? fetchedCountries
  const isLoading = !countriesOverride && isFetching

  const options = countries?.map(country => ({
    label: country.name,
    value: country.code,
    flag: getFlagUrl(country.code),
  })) || []

  // If control is provided, use react-hook-form Controller
  if (control && name) {
    return (
      <Stack spacing={0} sx={{ flex: 1 }}>
        {label && (
          <Typography variant="body3" mb={2}>
            {label}
          </Typography>
        )}
        <Controller
          name={name}
          control={control}
          defaultValue={defaultValue || ''}
          render={({ field }) => {
            const selectedOption = options.find(opt => opt.value === field.value) || null

            return (
              <Autocomplete
                options={options}
                value={selectedOption}
                onChange={(_, newValue) => {
                  field.onChange(newValue?.value || '')
                }}
                disabled={isLoading || disabled}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                popupIcon={<ChevronDownIcon strokeWidth={1} style={{ width: 20, height: 20 }} />}
                slotProps={countryMenuSlotProps}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={isLoading ? 'Loading countries...' : placeholder}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: selectedOption?.flag ? (
                        <Box
                          component="img"
                          src={selectedOption.flag}
                          alt={`${selectedOption.label} flag`}
                          sx={{
                            width: 20,
                            height: 15,
                            objectFit: 'cover',
                            borderRadius: '2px',
                            mr: 2,
                            ml: 0.5,
                          }}
                        />
                      ) : undefined,
                    }}
                    sx={fieldSx}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option.value} style={{ ...((props as any).style || {}), minHeight: '40px', padding: '8px 12px' }}>
                    <Box display="flex" alignItems="center" gap={2}>
                      <img
                        src={option.flag}
                        alt={`${option.label} flag`}
                        style={{
                          width: '20px',
                          height: '15px',
                          objectFit: 'cover',
                          borderRadius: '2px',
                          display: 'block'
                        }}
                      />
                      <Typography variant="body2">{option.label}</Typography>
                    </Box>
                  </li>
                )}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    padding: '0 !important',
                  },
                  '& .MuiAutocomplete-popupIndicator': {
                    transform: 'none',
                  },
                  '& .MuiAutocomplete-endAdornment': {
                    right: '12px !important',
                  },
                  '& .MuiAutocomplete-option': {
                    minHeight: '40px !important',
                    padding: '8px 12px !important',
                  },
                }}
              />
            )
          }}
        />

        {error && (
          <Typography variant="body4" color="error" mt="4px">
            {error?.message}
          </Typography>
        )}
      </Stack>
    )
  }

  // Otherwise, use as controlled component
  const selectedOption = options.find(opt => opt.value === value) || null

  return (
    <Stack spacing={0} sx={{ flex: 1 }}>
      {label && (
        <Typography variant="body3" mb={2}>
          {label}
        </Typography>
      )}
      <Autocomplete
        options={options}
        value={selectedOption}
        onChange={(_, newValue) => {
          onChange?.(newValue?.value || '')
        }}
        disabled={isLoading || disabled}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        popupIcon={<ChevronDownIcon strokeWidth={1} style={{ width: 20, height: 20 }} />}
        slotProps={countryMenuSlotProps}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={isLoading ? 'Loading countries...' : placeholder}
            InputProps={{
              ...params.InputProps,
              startAdornment: selectedOption?.flag ? (
                <Box
                  component="img"
                  src={selectedOption.flag}
                  alt={`${selectedOption.label} flag`}
                  onError={(e: any) => { e.currentTarget.style.visibility = 'hidden' }}
                  sx={{
                    width: 20,
                    height: 15,
                    objectFit: 'cover',
                    borderRadius: '2px',
                    // VERA-1095 (item 7): 8px, not the previous 4px — the flag
                    // and the country name sat too close together.
                    mr: 2,
                    ml: 0.5,
                  }}
                />
              ) : undefined,
            }}
            sx={fieldSx}
          />
        )}
        renderOption={(props, option) => (
          <li {...props} key={option.value} style={{ ...((props as any).style || {}), minHeight: '40px', padding: '8px 12px' }}>
            <Box display="flex" alignItems="center" gap={2}>
              <img
                src={option.flag}
                alt={`${option.label} flag`}
                onError={(e: any) => { e.currentTarget.style.visibility = 'hidden' }}
                style={{
                  width: '20px',
                  height: '15px',
                  objectFit: 'cover',
                  borderRadius: '2px',
                  display: 'block'
                }}
              />
              <Typography variant="body2">{option.label}</Typography>
            </Box>
          </li>
        )}
        sx={{
          '& .MuiOutlinedInput-root': {
            padding: '0 !important',
          },
          '& .MuiAutocomplete-popupIndicator': {
            transform: 'none',
          },
          '& .MuiAutocomplete-endAdornment': {
            right: '12px !important',
          },
          '& .MuiAutocomplete-option': {
            minHeight: '40px !important',
            padding: '8px 12px !important',
          },
        }}
      />

      {error && (
        <Typography variant="body4" color="error" mt="4px">
          {error?.message}
        </Typography>
      )}
    </Stack>
  )
}
